import { useState } from "react";
import { Popover } from "@base-ui/react/popover";
import { Bell, CheckCheck, X } from "lucide-react";
import { Link } from "react-router";
import { toast } from "sonner";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAppData } from "@/data/data-context";
import { cn, relativeDate } from "@/lib/utils";

export function NotificationInbox() {
  const { notifications, markAllRead } = useAppData();
  const [open, setOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const unread = notifications.filter((item) => !item.read).length;

  async function markRead() {
    setMarkingRead(true);
    try {
      await markAllRead();
    } catch {
      toast.error("Couldn't mark notifications as read. Try again.");
    } finally {
      setMarkingRead(false);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={<Button variant="ghost" size="icon" className="relative" />}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        title="Notifications"
      >
        <Bell className="size-4" />
        {unread > 0 && <span aria-hidden="true" className="absolute right-0 top-0 flex min-w-4 items-center justify-center rounded-full bg-orange px-1 text-[10px] font-bold leading-4 text-[#232323]">{unread}</span>}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="end" sideOffset={8} collisionPadding={12} className="z-50">
          <Popover.Popup className="w-[min(24rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-xl focus:outline-none">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <Popover.Title className="text-sm font-bold">Notifications</Popover.Title>
              <Popover.Close render={<Button variant="ghost" size="icon" />} aria-label="Close notifications"><X className="size-4" /></Popover.Close>
            </div>
            <div className="max-h-[min(24rem,55dvh)] overflow-y-auto overscroll-contain">
              {notifications.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <Bell className="mx-auto mb-3 size-6 text-muted-foreground" />
                  <p className="text-sm font-bold">No notifications yet</p>
                  <p className="mt-2 text-xs text-muted-foreground">Updates about your app builds, approvals, and releases will appear here.</p>
                </div>
              ) : notifications.slice(0, 8).map((item) => (
                <article key={item.id} className={cn("border-b border-border px-4 py-3 last:border-b-0", !item.read && "bg-primary/[0.035]")}>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold">{item.title}</h3>
                    {!item.read && <span role="img" className="size-2 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
                  </div>
                  <p className="mt-1 break-words text-sm text-muted-foreground">{item.body}</p>
                  <p className="mt-2 text-xs text-muted-foreground">{relativeDate(item.createdAt)}</p>
                </article>
              ))}
            </div>
            {notifications.length > 0 && <div className="flex items-center justify-between gap-2 border-t border-border p-3">
              <Button variant="ghost" size="sm" disabled={unread === 0 || markingRead} onClick={() => void markRead()}><CheckCheck className="size-4" />Mark all read</Button>
              <Link to="/notifications" className={buttonVariants({ variant: "ghost", size: "sm" })} onClick={() => setOpen(false)}>View all</Link>
            </div>}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
